/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { BEResponseListGetUfficioResponse } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce una lista di uffici
 * @summary Ricerca uffici
 */
export const getBackUffici = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListGetUfficioResponse>(
    { url: `/framework-bo-procedimenti/uffici`, method: 'get', signal },
    options,
  );
};

export const getGetBackUfficiQueryKey = () =>
  [`/framework-bo-procedimenti/uffici`] as const;

export const getGetBackUfficiQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackUffici>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackUffici>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getBackUffici>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBackUfficiQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBackUffici>>> = ({
    signal,
  }) => getBackUffici(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackUfficiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackUffici>>
>;
export type GetBackUfficiQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca uffici
 */
export const useGetBackUffici = <
  TData = Awaited<ReturnType<typeof getBackUffici>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackUffici>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackUfficiQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce un unico ufficio
 * @summary Dati ufficio
 */
export const getBackUfficiId = (
  idUfficio: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseListGetUfficioResponse>(
    {
      url: `/framework-bo-procedimenti/uffici/${idUfficio}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackUfficiIdQueryKey = (idUfficio: string) =>
  [`/framework-bo-procedimenti/uffici/${idUfficio}`] as const;

export const getGetBackUfficiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackUfficiId>>,
  TError = ErrorType<unknown>,
>(
  idUfficio: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackUfficiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackUfficiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackUfficiIdQueryKey(idUfficio);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBackUfficiId>>> = ({
    signal,
  }) => getBackUfficiId(idUfficio, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idUfficio, ...queryOptions };
};

export type GetBackUfficiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackUfficiId>>
>;
export type GetBackUfficiIdQueryError = ErrorType<unknown>;

/**
 * @summary Dati ufficio
 */
export const useGetBackUfficiId = <
  TData = Awaited<ReturnType<typeof getBackUfficiId>>,
  TError = ErrorType<unknown>,
>(
  idUfficio: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackUfficiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackUfficiIdQueryOptions(idUfficio, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
